/**
 * @fileoverview Elegant preloader component with animated curtain exit effect.
 * Features sequential curtain animations, progress indication, and minimum duration control.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants } from 'motion/react';

/**
 * Interface for preloader component properties.
 */
interface PreloaderProps {
  /** Whether the preloader should start completing its cycle */
  shouldStart?: boolean;
  /** Callback function called when preloader completes */
  onComplete?: () => void;
  /** Loading progress percentage (0-100) - shows progress bar if > 0 */
  progress?: number;
  /** Minimum display duration in milliseconds */
  minDuration?: number;
}

/**
 * Elegant preloader with sequential curtain exit animation.
 *
 * @description Creates a full-screen preloader that splits into vertical curtains
 * and animates upward to reveal content. Features optional progress indication,
 * minimum duration control, and smooth staggered exit animations.
 *
 * @param {PreloaderProps} props - Component properties
 * @param {boolean} [props.shouldStart=true] - Whether to start the completion sequence
 * @param {() => void} [props.onComplete] - Callback when animation completes
 * @param {number} [props.progress=0] - Loading progress (0-100), shows bar if > 0
 * @param {number} [props.minDuration=1500] - Minimum display time in milliseconds
 *
 * @returns {JSX.Element | null} Preloader component or null when complete
 *
 * @example
 * ```tsx
 * // Basic preloader
 * <Preloader onComplete={() => setAppReady(true)} />
 *
 * // With progress tracking
 * <Preloader
 *   progress={loadingProgress}
 *   shouldStart={assetsLoaded}
 *   onComplete={handleLoadComplete}
 *   minDuration={2000}
 * />
 *
 * // Asset loading integration
 * <Preloader
 *   progress={assetProgress}
 *   shouldStart={allAssetsLoaded}
 *   onComplete={() => setShowApp(true)}
 * />
 * ```
 */
const Preloader: React.FC<PreloaderProps> = ({
  shouldStart = true,
  onComplete,
  progress = 0,
  minDuration = 1500,
}) => {
  /** Controls whether preloader is currently visible */
  const [loading, setLoading] = useState(true);

  /** Tracks whether minimum display time has elapsed */
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  /**
   * Effect for minimum duration timer.
   * Ensures preloader displays for at least the specified duration
   * to provide smooth user experience and prevent flashing.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, minDuration);

    return () => clearTimeout(timer);
  }, [minDuration]);

  /**
   * Effect for completing the preloader sequence.
   * Only proceeds when both shouldStart is true AND minimum time has elapsed.
   */
  useEffect(() => {
    if (shouldStart && minTimeElapsed) {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        setLoading(false);
        if (onComplete) onComplete();
      }, 100);
    }
  }, [shouldStart, minTimeElapsed, onComplete]);

  /** Container animation variants for staggered curtain exit */
  const containerVariants = {
    initial: {},
    exit: {
      transition: {
        staggerChildren: 0.15, // Delay between each curtain animation
        delayChildren: 0.1, // Initial delay before starting
      },
    },
  };

  /** Individual curtain animation variants */
  const curtainVariants: Variants = {
    initial: {
      y: '0%', // Start at normal position
    },
    exit: {
      y: '-100%', // Exit upward off-screen
      transition: {
        duration: 0.5,
        ease: [0.76, 0, 0.24, 1], // Custom easing curve
      },
    },
  };

  return (
    <AnimatePresence>
      {loading && (
        <motion.div
          className="fixed inset-0 z-[9999] pointer-events-none"
          variants={containerVariants}
          initial="initial"
          exit="exit"
        >
          {/* Four vertical curtain sections that animate sequentially */}
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute bg-black"
              style={{
                left: `${i * 25}%`, // Position each curtain: 0%, 25%, 50%, 75%
                width: '25%', // Each curtain takes 25% width
                height: '100%', // Full viewport height
                top: 0,
              }}
              variants={curtainVariants}
            />
          ))}

          {/* Optional progress indicator - only shows when progress is meaningful */}
          {progress > 0 && progress < 100 && (
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 text-sm font-urbanist">
              <div className="flex items-center space-x-4">
                {/* Progress percentage */}
                <span>{Math.round(progress)}%</span>

                {/* Progress bar */}
                <div className="w-32 h-[1px] bg-white/20">
                  <div
                    className="h-full bg-white transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
